//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  DefaultButton,
  FlatDefaultIconButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import { SubtitleText } from '@components/atoms/text/Text';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import ItemId from '@models/ItemId';
import { UniversalItemType } from '@models/UniversalItemType';
import getAngularInjector from '@react-bridge/getAngularInjector';
import { isDefined } from '@utils/filterPredicates';
import { openUia } from '@utils/uia';

const CTA_CLICKED = 'universal-upload:cta-clicked';

const HOME_VIEW_NAME = 'Home';

export interface MediaPanelHeaderProps {
  selectedItems: Map<string, IContentLibraryItem>;
  onClose: () => void;
}

export interface UniversalImageOptions {
  itemType: UniversalItemType;
  itemId: ItemId;
}

export interface UniversalImageActionsService {
  launch: (view: string, options: UniversalImageOptions) => void;
}

export const MediaPanelHeader = ({
  selectedItems,
  onClose,
}: MediaPanelHeaderProps) => {
  const settings = useSelector((state) => state.contentDrawer.drawerSettings);

  const onOpen = () => {
    const items = Array.from(selectedItems.values());
    const firstSelectedItem = items[0];

    if (!settings.ctaText && isDefined(firstSelectedItem)) {
      // Open UIA if there is no cta button and we have a defined Item
      openUia(
        {
          itemType: firstSelectedItem.itemType,
          itemId: firstSelectedItem.id,
        },
        HOME_VIEW_NAME,
      );
    } else {
      if (isDefined(settings.broadcastEventOverride)) {
        // If there is a broadcastEventOverride defined then call that with all selected items (e.g. adding media to a scheduled post from post details page)
        settings.broadcastEventOverride(items);
      } else {
        // Otherwise call the generic CTA_CLICKED event using the angular broadcast event method with all the items (currently set for instagram scheduling from the old Like2Buy v1 page and dyanmic-ads)
        // TODO: PD-244569 This should be deprecated in favor of explicit definition of broadcastEventOverride
        const $rootScope = getAngularInjector().get('$rootScope');
        $rootScope.$broadcast(CTA_CLICKED, items);
      }
    }
    onClose();
  };

  const ctaText = settings.ctaText ?? 'Open';

  return (
    <Header>
      <FlatDefaultIconButton
        iconName='close'
        trackingId={ContentDrawerTrackingIds.CLOSE_DRAWER}
        onClick={onClose}
      />
      <CenteredHeaderText>
        <SubtitleText>Media Library</SubtitleText>
      </CenteredHeaderText>
      {selectedItems.size > 0 ? (
        <PrimaryButton
          trackingId={ContentDrawerTrackingIds.USE_SELECTED_MEDIA}
          onClick={onOpen}
        >
          {ctaText}
        </PrimaryButton>
      ) : (
        <DefaultButton isDisabled={true}>{ctaText}</DefaultButton>
      )}
    </Header>
  );
};

const Header = styled.header(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacings[3]};
    background: ${theme.colors.gray0};
  `,
);

const CenteredHeaderText = styled.h2(
  ({ theme }) => `
    && {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: ${theme.spacings[0]};
    }
  `,
);
